/*
 * High-Risk Users page.
 *
 * Full user roster with funnel counts and the overall risk score,
 * paginated and sortable on every column (default: click rate DESC).
 * Inherited scope applies; export via the table's own export menu.
 *
 * Calculations:
 *   - clickRate = clicked / sent; reportRate = reported / sent.
 *   - risk = the user's overall 0-100 risk score (risk_snapshot via the
 *     users aggregation when unscoped, client model otherwise).
 *
 * Integration hook: the `users` aggregation (uncapped, limit 5000).
 */
import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { motion } from 'motion/react';
import { USERS, HIGH_RISK_USERS, DATA_EPOCH } from '../data';
import useDashboardData from '../hooks/useDashboardData';
import ActiveFilters from './ActiveFilters';
import DataTable from './DataTable';

const isIn = (val, vs) => { if (!vs) return true; const a = Array.isArray(val) ? val : [val]; return Array.isArray(vs) ? a.some((x) => vs.includes(x)) : a.includes(vs); };

// Lazy + epoch-tracked so the funnel map follows the live hydrated data.
let funnelCache = null;
let funnelEpoch = -1;
const FUNNEL_BY_EMAIL = {
    get(email) {
        if (funnelEpoch !== DATA_EPOCH) {
            funnelCache = new Map(HIGH_RISK_USERS.map((u) => [u.email, u]));
            funnelEpoch = DATA_EPOCH;
        }
        return funnelCache.get(email);
    },
};
const ZERO_FUNNEL = { sent: 0, clicked: 0, secondStage: 0, reported: 0 };

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

// Canonical High Click Users columns, shared with the Users card on the
// Simulations page so the preview and the drill can't drift apart.
export function highClickUserColumns() {
    return [
        { id: 'name',       label: 'Name',       always: true,   sortable: true, type: 'string', accessor: (u) => u.name },
        { id: 'email',      label: 'Email',      defaultOn: true,sortable: true, type: 'string', accessor: (u) => u.email },
        { id: 'risk',       label: 'Risk Score', defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.risk ?? -1, align: 'right',
          render: (u) => u.risk == null
              ? <Box component="span" sx={{ color: 'text.disabled', fontWeight: 500 }}>Unscored</Box>
              : <Box component="span" sx={{ fontWeight: 700, color: u.risk >= 56 ? 'var(--red)' : u.risk >= 30 ? 'var(--amber)' : 'var(--green)' }}>{u.risk}</Box> },
        { id: 'sent',       label: 'Sent',       defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.sent, align: 'right' },
        { id: 'clicked',    label: 'Clicked',    defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.clicked, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 700, color: u.clicked > 2 ? 'var(--red)' : u.clicked > 0 ? 'var(--amber)' : 'var(--green)' }}>{u.clicked}</Box> },
        { id: 'secondStage',label: '2nd Stage',  defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.secondStage, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 700, color: u.secondStage > 0 ? 'var(--red)' : 'text.secondary' }}>{u.secondStage}</Box> },
        { id: 'reported',   label: 'Reported',   defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.reported, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 700, color: u.reported > 0 ? 'var(--green)' : 'text.secondary' }}>{u.reported}</Box> },
        { id: 'clickRate',  label: 'Click Rate', defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.clickRate, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 600, color: u.clickRate >= 30 ? 'var(--red)' : u.clickRate >= 15 ? 'var(--amber)' : 'var(--green)' }}>{u.clickRate}%</Box> },
        { id: 'reportRate', label: 'Report Rate',defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.reportRate, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 600, color: u.reportRate >= 15 ? 'var(--green)' : 'text.secondary' }}>{u.reportRate}%</Box> },
    ];
}

export default function HighRiskUsersPage({ drill, scope, removeScope }) {
    // Uncapped risk-ranked users from the rollup. Tenancy is a server-supported
    // pooling dimension, so it is forwarded to the rollup (below) and a
    // tenancy-only filter still reads the aggregate; only the client-side
    // dimensions (dept/location/…) force the raw-roster fallback.
    const nonTenancyScopeActive = !!(scope && Object.keys(scope).some((k) => k !== 'tenancy' && (Array.isArray(scope[k]) ? scope[k].length > 0 : !!scope[k])));
    const tenancyScope = Array.isArray(scope?.tenancy) && scope.tenancy.length ? { tenancy: scope.tenancy } : undefined;
    const riskUsersQ = useDashboardData({ metric: 'simulations', aggregation: 'users', limit: 5000, scope: tenancyScope });
    const aggRows = (!nonTenancyScopeActive && Array.isArray(riskUsersQ.data?.riskUsers)) ? riskUsersQ.data.riskUsers.map((u) => ({
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email, email: u.email, dept: u.dept || '', location: u.location || '',
        risk: u.score ?? null,
        sent: u.sent, clicked: u.clicked, secondStage: u.secondStage, reported: u.reported,
        clickRate: u.sent ? Math.round((u.clicked / u.sent) * 100) : 0, reportRate: u.sent ? Math.round((u.reported / u.sent) * 100) : 0,
    })) : null;

    const baseUsers = useMemo(() => {
        const s = scope || {};
        return (aggRows || USERS).filter((u) => {
            if (!isIn(u.dept,     s.department)) return false;
            if (!isIn(u.location, s.location))   return false;
            if (!isIn(u.city,     s.city))       return false;
            if (!isIn(u.country,  s.country))    return false;
            if (!isIn(u.company,  s.company))    return false;
            if (!isIn(u.state,    s.state))      return false;
            if (!isIn(u.adGroups,  s.adGroup))    return false;
            if (s.user) {
                const userVals = Array.isArray(s.user) ? s.user : [s.user];
                if (!userVals.some((v) => u.email === v || u.name === v)) return false;
            }
            return true;
        });
    }, [scope, aggRows]);

    // Pre-merge funnel stats into the row so DataTable's accessor +
    // sortable headers can read funnel fields the same way they read
    // any other column.
    const rows = useMemo(() => baseUsers.map((u) => {
        if (aggRows) return u;
        const f = FUNNEL_BY_EMAIL.get(u.email) || ZERO_FUNNEL;
        const clickRate = f.sent ? Math.round((f.clicked / f.sent) * 100) : 0;
        const reportRate = f.sent ? Math.round((f.reported / f.sent) * 100) : 0;
        return { ...u, ...f, clickRate, reportRate };
    }), [baseUsers, aggRows]);

    const columns = highClickUserColumns();

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
            <motion.div variants={fadeUp}>
                <DataTable
                    title="High Click Users"
                    subtitle={`${rows.length.toLocaleString()} users`}
                    columns={columns}
                    rows={rows}
                    rowKey={(u) => u.email || u.name}
                    onRowClick={(u) => drill('user-detail', u.name, { type: 'user', value: u.email || u.name })}
                    initialSort={{ by: 'clickRate', dir: 'desc' }}
                    storageKey="rbx-high-risk-cols-v2"
                    emptyMessage="No users in scope"
                />
            </motion.div>
        </motion.div>
    );
}
